import React, { useContext, useState, useEffect } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Complaint.css";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import ComplaintsSidebar from "./ComplaintsSidebar";
import axios from "axios";
import moment from "moment";

import { UserContext } from "../../../../contexts/User";
import { SettingsContext } from "../../../../contexts/Settings";
import { RestaurantContext } from "../../../../contexts/Restaurant";
import { AuditContext } from "../../../../contexts/Audit";
import { BASE_URL } from "../../../../BaseUrl";

import { _t, getSystemSettings, getCookie } from "../../../../functions/Functions";
import { ToastError, ToastSuccess } from "../../../../functions/ToastShow";
import CustomSpinLoader from "../../../components/CustomSpinLoader";

const AddNewComplaint = () => {
  const { t } = useTranslation();
  let { generalSettings } = useContext(SettingsContext);
  let { authUserInfo, adminStaffForSearch } = useContext(UserContext);
  const { orderTypeForSearch, branchForSearch } = useContext(RestaurantContext);
  const { loading, setLoading, complaintCategories, getComplaintCategories } = useContext(AuditContext);

  useEffect(() => {
    if (!complaintCategories) {
      getComplaintCategories();
    }
  }, []);

  // Centralized Form State
  const [formData, setFormData] = useState({
    branch: null,
    orderType: null,
    billNo: "",
    phoneNo: "",
    loadedOrder: null,
    selectedItems: [],
    complaintNatures: [],
    upsetLevel: "serious", // "normal", "serious", "very_upset"
    priority: "high", // "low", "medium", "high", "critical"
    guestSeated: 1, // 1 for yes, 0 for no
    guestComments: "",
    reportedVia: "Guest told us directly",
    attachment: null,
    date: null,
    assignedTo: null
  });

  const [searchedOrders, setSearchedOrders] = useState([]);
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const updateForm = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSelectOrder = (order) => {
    if (order.has_complaint) {
      ToastError({ title: "Complaint already registered for this order." });
      return;
    }

    let newOrderType = formData.orderType;
    if (order.order_type_id && orderTypeForSearch) {
      const matchedType = orderTypeForSearch.find(t => t.id === order.order_type_id);
      if (matchedType) newOrderType = matchedType;
    }

    let newBranch = formData.branch;
    if (order.branch_id && branchForSearch) {
      const matchedBranch = branchForSearch.find(b => b.id === order.branch_id);
      if (matchedBranch) {
        newBranch = { label: matchedBranch.name, value: matchedBranch.id, original: matchedBranch };
      }
    }

    setFormData(prev => ({
      ...prev,
      loadedOrder: order,
      selectedItems: [],
      orderType: newOrderType,
      branch: newBranch || prev.branch
    }));
  };

  const handleLoadOrder = async () => {
    const billNoVal = formData.billNo ? formData.billNo.trim() : "";
    const phoneNoVal = formData.phoneNo ? formData.phoneNo.trim() : "";

    if (!billNoVal && !phoneNoVal) {
      ToastError({ title: "Please enter Order / Bill no or Customer Phone No." });
      return;
    }
    
    setLoading(true);
    let params = [];

    if (billNoVal) {
      if (/^\d+$/.test(billNoVal)) {
        params.push(`order_group_id=${encodeURIComponent(billNoVal)}`);
      } else {
        params.push(`token=${encodeURIComponent(billNoVal)}`);
      }
    }

    if (phoneNoVal) {
      params.push(`phn_no=${encodeURIComponent(phoneNoVal)}`);
      params.push(`customer_phone=${encodeURIComponent(phoneNoVal)}`);
    }

    if (formData.branch && formData.branch.value) {
      params.push(`branch_id=${encodeURIComponent(formData.branch.value)}`);
    }

    if (formData.date) {
      const searchDate = moment(formData.date).format("YYYY-MM-DD");
      params.push(`order_date=${encodeURIComponent(searchDate)}`);
    }

    const url = `${BASE_URL}/complaints/find-order?${params.join('&')}`;

    try {
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${getCookie()}` } });
      
      let orders = [];
      if (Array.isArray(res.data?.data)) {
        orders = res.data.data;
      } else if (Array.isArray(res.data?.orders)) {
        orders = res.data.orders;
      } else if (Array.isArray(res.data)) {
        orders = res.data;
      } else if (res.data?.data && typeof res.data.data === 'object') {
        orders = [res.data.data];
      } else if (res.data && typeof res.data === 'object' && (res.data.order_group_id || res.data.id)) {
        orders = [res.data];
      }

      if (orders.length > 0) {
        setSearchedOrders(orders);
        if (orders.length === 1) {
          if (orders[0].has_complaint) {
            ToastError({ title: "Complaint already registered for this order." });
            updateForm("loadedOrder", null);
          } else {
            handleSelectOrder(orders[0]);
            ToastSuccess({ title: "Order loaded successfully" });
          }
        } else {
          // Multiple orders found - reset loadedOrder so user can pick from modern UI
          updateForm("loadedOrder", null);
          ToastSuccess({ title: `Found ${orders.length} orders. Please select one.` });
        }
      } else {
        setSearchedOrders([]);
        updateForm("loadedOrder", null);
        ToastError({ title: res.data?.message || "Order not found" });
      }
    } catch (error) {
      console.error(error);
      setSearchedOrders([]);
      updateForm("loadedOrder", null);
      ToastError({ title: error.response?.data?.message || "Error finding order." });
    }
    setLoading(false);
  };

  const handleSave = async () => {
    let errors = {};
    if (!formData.upsetLevel) errors.upsetLevel = "Required";
    if (!formData.loadedOrder) errors.order = "Load an order first";
    if (formData.complaintNatures.length === 0) errors.nature = "Select complaint nature";
    if (!formData.assignedTo) errors.assignedTo = "Assignee is required";
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      ToastError({ title: "Please fill all required fields: Load order, Nature of complaint, and Assign To." });
      return;
    }
    
    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("order_group_id", formData.loadedOrder.order_group_id || formData.loadedOrder.id);
      payload.append("remarks", formData.guestComments || "");
      payload.append("severity", formData.upsetLevel);
      payload.append("priority", formData.priority);
      payload.append("guest_seated", formData.guestSeated);
      payload.append("assigned_to", formData.assignedTo.id);
      payload.append("complaint_date", formData.date ? moment(formData.date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"));
      payload.append("reported_via", formData.reportedVia);

      const subCategories = formData.complaintNatures.map(n => n.id);
      payload.append("sub_categories", JSON.stringify(subCategories));

      if (selectedParentCategory) {
        payload.append("category_id", selectedParentCategory.id);
      }

      const itemsArray = formData.selectedItems.map(item => ({
        item_id: item.food_item_id || item.item_id,
        quantity: item.quantity || 1
      }));
      payload.append("items", JSON.stringify(itemsArray));

      if (formData.attachment) {
        payload.append("image", formData.attachment);
      }

      const res = await axios.post(BASE_URL + "/complaints", payload, {
        headers: { Authorization: `Bearer ${getCookie()}` }
      });

      if (res.data && res.data.status) {
        ToastSuccess({ title: "Complaint saved successfully!" });
        setFormData({
          branch: null, orderType: null, billNo: "", phoneNo: "", loadedOrder: null, selectedItems: [], complaintNatures: [], upsetLevel: "serious", priority: "high", guestSeated: 1, guestComments: "", reportedVia: "Guest told us directly", attachment: null, date: null, assignedTo: null
        });
        setSearchedOrders([]);
        setSelectedParentCategory(null);
      } else {
        ToastError({ title: res.data?.message || "Failed to save complaint" });
      }
    } catch (error) {
      console.error(error);
      ToastError({ title: "Failed to save complaint" });
    }
    setLoading(false);
  };

  const toggleItem = (itemObj) => {
    const idKey = itemObj.food_item_id || itemObj.item_id;
    const exists = formData.selectedItems.find(i => (i.food_item_id || i.item_id) === idKey);
    if (exists) {
      updateForm("selectedItems", formData.selectedItems.filter(i => (i.food_item_id || i.item_id) !== idKey));
    } else {
      updateForm("selectedItems", [...formData.selectedItems, itemObj]);
    }
  };

  const toggleNature = (natureObj) => {
    const exists = formData.complaintNatures.find(n => n.id === natureObj.id);
    if (exists) {
      updateForm("complaintNatures", formData.complaintNatures.filter(n => n.id !== natureObj.id));
    } else {
      const objWithCat = {
        ...natureObj,
        category_id: natureObj.category_id || selectedParentCategory?.id
      };
      updateForm("complaintNatures", [...formData.complaintNatures, objWithCat]);
    }
  };

  const getCategoryColor = (category, index = 0) => {
    if (category?.color_code) return category.color_code;
    if (category?.color) {
      const namedColors = {
        orange: "#f59e0b",
        blue: "#3b82f6",
        purple: "#a855f7",
        green: "#10b981",
        red: "#ef4444",
        teal: "#14b8a6",
        indigo: "#6366f1",
        pink: "#ec4899"
      };
      if (namedColors[category.color.toLowerCase()]) {
        return namedColors[category.color.toLowerCase()];
      }
      return category.color;
    }
    const name = (category?.name || "").toLowerCase().trim();
    if (name.includes("kitchen") || name.includes("food")) return "#f59e0b"; // Orange
    if (name.includes("service") || name.includes("waiter")) return "#3b82f6"; // Blue
    if (name.includes("bill") || name.includes("cash") || name.includes("account")) return "#a855f7"; // Purple
    if (name.includes("deliver") || name.includes("rider")) return "#10b981"; // Green
    if (name.includes("house") || name.includes("clean") || name.includes("hygiene")) return "#ef4444"; // Red
    
    const colors = ["#f59e0b", "#3b82f6", "#a855f7", "#10b981", "#ef4444", "#14b8a6", "#6366f1", "#ec4899"];
    return colors[index % colors.length];
  };

  return (
    <>
      <Helmet>
        <title>
          {_t(t("Complaint Management"))} - {generalSettings && getSystemSettings(generalSettings, "siteName")}
        </title>
      </Helmet>
      
      <main id="main" data-simplebar>
        
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ComplaintsSidebar />
            </div>

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              
              <div className="t-bg-white complaint-main-card">
                <CustomSpinLoader loading={loading || authUserInfo.permissions === null} />
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                <div className="complaint-panel">
                  {/* 1. Find the order */}
                  <div className="complaint-section-box">
                    <h5>1. Find the order</h5>
                    <p className="sub-text">Search by Order / Bill no or Customer Phone. Date and Branch are optional filters.</p>
                    
                    <div className="row g-2 align-items-end">
                      <div className="col-12 col-md-4 col-lg-2 mb-2">
                        <label className="c-label">Date <span className="c-label-optional">(Optional)</span></label>
                        <DatePicker
                          selected={formData.date}
                          onChange={(date) => updateForm('date', date)}
                          className="c-input"
                          dateFormat="yyyy-MM-dd"
                          isClearable
                          placeholderText="Any date"
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 mb-2">
                        <label className="c-label">Branch <span className="c-label-optional">(Optional)</span></label>
                        <Select
                          classNamePrefix="select"
                          options={branchForSearch ? branchForSearch.map(b => ({ label: b.name, value: b.id, original: b })) : []}
                          value={formData.branch}
                          onChange={(selected) => updateForm('branch', selected)}
                          placeholder="All branches"
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 mb-2">
                        <label className="c-label">Order / bill no.</label>
                        <input 
                          type="text" 
                          className="c-input" 
                          value={formData.billNo} 
                          onChange={(e) => updateForm('billNo', e.target.value)} 
                          placeholder="e.g. 1054 or U4-6" 
                          onKeyDown={(e) => { if (e.key === 'Enter') handleLoadOrder(); }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-2 mb-2">
                        <label className="c-label">Customer Phone No</label>
                        <input 
                          type="text" 
                          className="c-input" 
                          value={formData.phoneNo} 
                          onChange={(e) => updateForm('phoneNo', e.target.value)} 
                          placeholder="e.g. 03001234567" 
                          onKeyDown={(e) => { if (e.key === 'Enter') handleLoadOrder(); }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-2 mb-2">
                        <button className="c-btn-load w-100" onClick={handleLoadOrder} disabled={loading}>
                          Load order
                        </button>
                      </div>
                    </div>
                    
                    <small className="c-hint">
                      * Mandatory: Enter either <strong>Order / Bill no</strong> or <strong>Customer Phone No</strong>. Date and branch are optional.
                    </small>

                    {/* Multi-Order Results List in Modern UI */}
                    {searchedOrders && searchedOrders.length > 0 && (
                      <div className="order-results-header">
                        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                          <h6 className="order-results-title mb-1">
                            Matching Orders ({searchedOrders.length} {searchedOrders.length === 1 ? 'order' : 'orders'} found) — Click to select:
                          </h6>
                          {formData.loadedOrder && (
                            <span className="order-results-selected-tag">
                              ✓ Selected: Order #{formData.loadedOrder.order_group_id || formData.loadedOrder.id}
                            </span>
                          )}
                        </div>

                        <div className="order-cards-grid">
                          {searchedOrders.map((order, idx) => {
                            const orderId = order.order_group_id || order.id;
                            const isSelected = formData.loadedOrder && (formData.loadedOrder.order_group_id === orderId || formData.loadedOrder.id === orderId);
                            const tokenNo = order.token || order.token_no || order.token_number || order.token_id || 'N/A';
                            const phoneNo = order.customer_phone || order.phn_no || order.phone_no || order.phone || 'N/A';
                            const billDisplay = order.bill_display || (order.total_price ? `Rs ${order.total_price}` : (order.grand_total ? `Rs ${order.grand_total}` : (order.bill_amount ? `Rs ${order.bill_amount}` : '')));
                            const customerName = order.customer_name || 'Walk-in';
                            const orderDate = order.punched_at || order.order_date || order.created_at || '';
                            const hasComplaint = !!(order.has_complaint || order.has_complaint === 1);

                            return (
                              <div
                                key={orderId || idx}
                                onClick={() => handleSelectOrder(order)}
                                className={`order-card-item ${isSelected ? 'selected' : ''}`}
                              >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span className="order-card-token">
                                    Token: {tokenNo}
                                  </span>
                                  <span className="order-card-id">
                                    ID: #{orderId}
                                  </span>
                                </div>

                                <div className="order-card-details">
                                  <div className="d-flex align-items-center mb-1">
                                    <span className="order-card-label">Phone:</span>
                                    <strong>{phoneNo}</strong>
                                  </div>
                                  <div className="d-flex align-items-center mb-1">
                                    <span className="order-card-label">Guest:</span>
                                    <span>{customerName}</span>
                                    {order.table_name && <span className="text-muted ml-1">({order.table_name})</span>}
                                  </div>
                                  {orderDate && (
                                    <div className="d-flex align-items-center mb-1 order-card-time">
                                      <span className="order-card-label">Time:</span>
                                      <span>{orderDate}</span>
                                    </div>
                                  )}
                                </div>

                                <div className="d-flex justify-content-between align-items-center order-card-footer">
                                  <span className="order-card-bill">
                                    {billDisplay}
                                  </span>
                                  {hasComplaint ? (
                                    <span className="order-card-status-logged">
                                      Complaint Logged
                                    </span>
                                  ) : isSelected ? (
                                    <span className="order-card-status-selected">
                                      ✓ Selected
                                    </span>
                                  ) : (
                                    <span className="order-card-action-select">
                                      Click to select &rarr;
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Order Summary when order is selected */}
                    {formData.loadedOrder && (
                      <div className="order-summary">
                        <div className="col-left">
                          <div>Order</div>
                          <div>Token</div>
                          <div>Branch</div>
                          <div>Order Type / Party</div>
                          <div>Waiter</div>
                          <div>Punched</div>
                          <div>Bill amount</div>
                          <div>Guest</div>
                        </div>
                        <div className="col-right text-right">
                          <div><strong>ID: {formData.loadedOrder.order_group_id || formData.loadedOrder.id} - {formData.loadedOrder.table_name || 'N/A'} - {formData.loadedOrder.total_guest || 0} guests</strong></div>
                          <div>{formData.loadedOrder.token || 'N/A'}</div>
                          <div>{formData.loadedOrder.branch_name || 'N/A'}</div>
                          <div>{formData.loadedOrder.order_type || 'N/A'} - {formData.loadedOrder.party_name || 'N/A'}</div>
                          <div>{formData.loadedOrder.waiter_name || 'N/A'}</div>
                          <div>{formData.loadedOrder.punched_at || formData.loadedOrder.order_date || ''} {formData.loadedOrder.time_ago ? `- ${formData.loadedOrder.time_ago}` : ''}</div>
                          <div><strong>{formData.loadedOrder.bill_display || (formData.loadedOrder.total_price ? `Rs ${formData.loadedOrder.total_price}` : '')}</strong></div>
                          <div>{formData.loadedOrder.customer_name || 'Walk-in'} - {formData.loadedOrder.customer_phone || formData.loadedOrder.phn_no || formData.loadedOrder.phone_no || ''}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* 2. Which item is this about? */}
                  {formData.loadedOrder && (
                    <div className="complaint-section-box">
                      <h5>2. Which item is this about?</h5>
                      <p className="sub-text">Select one or more items. If it's a service or billing issue, leave items unselected.</p>
                      
                      <div>
                        {formData.loadedOrder.items && formData.loadedOrder.items.length > 0 ? formData.loadedOrder.items.map((item, index) => (
                          <div 
                            key={index} 
                            className={`item-row ${formData.selectedItems.some(i => (i.food_item_id || i.item_id) === (item.food_item_id || item.item_id)) ? 'selected' : ''}`}
                            onClick={() => toggleItem(item)}
                          >
                            <div className="d-flex align-items-center">
                              <span className="custom-checkbox"></span>
                              <span>{item.quantity}x {item.food_item || item.name}</span>
                            </div>
                            <span className="c-item-price">Rs {item.price}</span>
                          </div>
                        )) : (
                          <div className="text-muted p-2">No items found in this order.</div>
                        )}
                      </div>
                      
                    </div>
                  )}

                  {/* 3. Nature of the complaint */}
                  <div className="complaint-section-box">
                    <h5>3. Nature of the complaint <span className="text-danger">*</span></h5>
                    <p className="sub-text">Select a category, then pick one or more complaint types.</p>
                    
                    {/* Parent Categories */}
                    <div className="mb-3">
                      <label className="c-label mb-2">Category</label>
                      <div className="c-btn-group mb-1">
                        {complaintCategories && complaintCategories.length > 0 ? (
                          complaintCategories.map((category, catIndex) => {
                            const isSelected = selectedParentCategory && selectedParentCategory.id === category.id;
                            const catColor = getCategoryColor(category, catIndex);
                            const selectedSubCount = (category.sub_categories || []).filter(sub => 
                              formData.complaintNatures.some(n => n.id === sub.id)
                            ).length;

                            return (
                              <button 
                                key={`cat-${category.id}`} 
                                type="button"
                                className={`c-btn-type c-category-btn ${isSelected ? 'active' : ''}`}
                                onClick={() => {
                                  if (!selectedParentCategory || selectedParentCategory.id !== category.id) {
                                    setSelectedParentCategory(category);
                                    updateForm("complaintNatures", []);
                                  }
                                }}
                                style={{
                                  borderColor: isSelected ? catColor : '#e2e8f0',
                                  backgroundColor: isSelected ? catColor : '#ffffff',
                                  boxShadow: isSelected ? `0 4px 6px -1px ${catColor}40` : 'none',
                                }}
                              >
                                <span 
                                  className="dot" 
                                  style={{ backgroundColor: isSelected ? '#ffffff' : catColor }}
                                ></span>
                                <span>{category.name}</span>
                                {selectedSubCount > 0 && (
                                  <span 
                                    className="c-category-count"
                                    style={{ 
                                      backgroundColor: isSelected ? '#ffffff' : catColor, 
                                      color: isSelected ? catColor : '#ffffff', 
                                    }}
                                  >
                                    {selectedSubCount}
                                  </span>
                                )}
                              </button>
                            );
                          })
                        ) : (
                          <div className="p-2 text-muted">Loading categories...</div>
                        )}
                      </div>
                    </div>

                    {/* Child Categories (Sub-categories) */}
                    {selectedParentCategory ? (
                      <div className="c-subcat-container">
                        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                          <label className="c-label mb-0">
                            Complaint Types for <strong>{selectedParentCategory.name}</strong>:
                          </label>
                          <small className="text-muted">Select all that apply</small>
                        </div>

                        <div className="nature-grid">
                          {selectedParentCategory.sub_categories && selectedParentCategory.sub_categories.length > 0 ? (
                            selectedParentCategory.sub_categories.map((sub) => {
                              const catIndex = complaintCategories ? complaintCategories.findIndex(c => c.id === selectedParentCategory.id) : 0;
                              const parentColor = getCategoryColor(selectedParentCategory, catIndex);
                              const isSubSelected = formData.complaintNatures.some(n => n.id === sub.id);

                              return (
                                <div 
                                  key={`subcat-${sub.id}`} 
                                  className={`nature-tag ${isSubSelected ? 'active' : ''}`}
                                  onClick={() => toggleNature(sub)}
                                  title={`${selectedParentCategory.name} - ${sub.name}`}
                                >
                                  <span 
                                    className="dot" 
                                    style={{ backgroundColor: parentColor }}
                                  ></span>
                                  {sub.name}
                                  {isSubSelected && <span className="ml-1 text-success font-weight-bold">✓</span>}
                                </div>
                              );
                            })
                          ) : (
                            <div className="p-2 text-muted">
                              No sub-categories available for {selectedParentCategory.name}.
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="c-subcat-empty">
                        Please select a parent category above to view and select complaint types.
                      </div>
                    )}

                  </div>
                  
                  {/* 4. Details */}
                  <div className="complaint-section-box">
                    <h5>4. Details</h5>
                    <div className="row mb-3">
                      <div className="col-12 col-lg-6 mb-3 mb-lg-0">
                        <label className="c-label">How upset is the guest <span className="text-danger">*</span></label>
                        <div className="c-btn-group mb-1">
                          <button className={`c-btn-type ${formData.upsetLevel === 'normal' ? 'active' : ''}`} onClick={() => updateForm('upsetLevel', 'normal')}>Normal</button>
                          <button className={`c-btn-type ${formData.upsetLevel === 'serious' ? 'active' : ''}`} onClick={() => updateForm('upsetLevel', 'serious')}>Serious</button>
                          <button className={`c-btn-type ${formData.upsetLevel === 'very_upset' ? 'active' : ''}`} onClick={() => updateForm('upsetLevel', 'very_upset')}>Very upset</button>
                        </div>
                        <small className="c-hint">Serious and above triggers an immediate manager alert.</small>
                      </div>
                      <div className="col-12 col-lg-6">
                        <label className="c-label">Is the guest still at the table?</label>
                        <div className="c-btn-group mb-1">
                          <button className={`c-btn-type ${formData.guestSeated === 1 ? 'active' : ''}`} onClick={() => updateForm('guestSeated', 1)}>Yes — still here</button>
                          <button className={`c-btn-type ${formData.guestSeated === 0 ? 'active' : ''}`} onClick={() => updateForm('guestSeated', 0)}>Already left</button>
                        </div>
                        <small className="c-hint">Guest still seated = 5-min SLA. Already left = 24 hours.</small>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-12 col-lg-6 mb-3 mb-lg-0">
                        <label className="c-label">Priority <span className="text-danger">*</span></label>
                        <div className="c-btn-group mb-1">
                          <button className={`c-btn-type ${formData.priority === 'low' ? 'active' : ''}`} onClick={() => updateForm('priority', 'low')}>Low</button>
                          <button className={`c-btn-type ${formData.priority === 'medium' ? 'active' : ''}`} onClick={() => updateForm('priority', 'medium')}>Medium</button>
                          <button className={`c-btn-type ${formData.priority === 'high' ? 'active' : ''}`} onClick={() => updateForm('priority', 'high')}>High</button>
                          <button className={`c-btn-type ${formData.priority === 'critical' ? 'active' : ''}`} onClick={() => updateForm('priority', 'critical')}>Critical</button>
                        </div>
                      </div>
                      <div className="col-12 col-lg-6">
                        <label className="c-label">Assign To <span className="text-danger">*</span></label>
                        <Select
                          classNamePrefix="select"
                          getOptionLabel={(option)=> option.name}
                          getOptionValue={(option)=> option.id}
                          options={adminStaffForSearch ? adminStaffForSearch : []}
                          value={formData.assignedTo}
                          onChange={(selected) => updateForm('assignedTo', selected)}
                          placeholder="Select Assignee"
                          isClearable
                        />
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label className="c-label">What the guest said</label>
                      <textarea 
                        className="c-input" 
                        rows="3" 
                        value={formData.guestComments} 
                        onChange={(e) => updateForm('guestComments', e.target.value)}
                      ></textarea>
                    </div>

                    <div className="row mb-2">
                      <div className="col-12 col-lg-6 mb-3 mb-lg-0">
                        <label className="c-label">Attach a photo</label>
                        <div className="d-flex align-items-center">
                          <input type="file" className="c-input p-1" onChange={(e) => setFormData({ ...formData, attachment: e.target.files[0] })} />
                        </div>
                        <small className="c-hint">Dish, packing, or bill photo — avoids disputes later.</small>
                      </div>
                      <div className="col-12 col-lg-6">
                        <label className="c-label">Reported via</label>
                        <select className="c-input" value={formData.reportedVia} onChange={(e) => updateForm('reportedVia', e.target.value)}>
                          <option value="Guest told us directly">Guest told us directly</option>
                          <option value="Call center">Call center</option>
                          <option value="Social Media">Social Media</option>
                        </select>
                      </div>
                    </div>
                    

                      <div className="action-btns pt-3 mt-4 border-top d-flex flex-wrap">
                        <button className="btn-cancel-complaint" disabled={loading}>Cancel</button>
                        <button className="btn-save-secondary" onClick={handleSave} disabled={loading}>Save & add another</button>
                        {/* <button className="btn-save-primary" onClick={handleSave} disabled={loading}>Save & alert manager</button> */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default AddNewComplaint;
